import React from 'react';
import { formatMeasuredOn, formatCount } from './format.js';

/**
 * The system's measurement primitive. Every number the product shows goes through
 * this component so that unknown, zero and not-yet-measured stay distinguishable.
 */
export function Statistic({
  label, value, of, noun, unit, state = 'measured', tone = 'neutral',
  size = 'md', sides = [], date, unknownText = 'unknown',
  placeholderText = 'not measured', className = '', ...rest
}) {
  /* Zero is a measurement, not a deviation: "0 of 5" is a complete, confident
     finding and renders exactly like any other measured value. Only two states
     deviate — unknown (no measurement possible) and placeholder (not measured yet). */
  const resolved = state === 'zero' ? 'measured' : state;
  const cls = [
    'sp-stat', 'sp-stat--' + size,
    tone !== 'neutral' && resolved !== 'unknown' && resolved !== 'placeholder' && 'sp-stat--' + tone,
    (resolved === 'unknown' || resolved === 'placeholder') && 'sp-stat--' + resolved,
    className
  ].filter(Boolean).join(' ');
  const measuredOn = formatMeasuredOn(date);

  let figure;
  if (resolved === 'unknown') {
    figure = <span className="sp-stat__value">{unknownText}</span>;
  } else if (resolved === 'placeholder') {
    figure = <span className="sp-stat__value">{'\u2014'}</span>;
  } else {
    figure = <span className="sp-stat__value">{formatCount(value)}{unit && <span className="sp-stat__unit">{unit}</span>}</span>;
  }

  return (
    <div className={cls} {...rest}>
      {label && <div className="sp-stat__label">{label}</div>}
      <div className="sp-stat__figure">
        {resolved === 'unknown' && <span className="sp-stat__mark" aria-hidden="true" />}
        {figure}
        {resolved !== 'placeholder' && of != null && (
          <span className="sp-stat__denom">
            of <span className="sp-stat__denom-num">{formatCount(of)}</span>{noun ? ' ' + noun : ''}
          </span>
        )}
        {resolved === 'placeholder' && (
          <span className="sp-badge sp-badge--placeholder sp-badge--sm">{placeholderText}</span>
        )}
      </div>
      {sides.length > 0 && resolved !== 'placeholder' && (
        <div className="sp-stat__sides">
          {sides.slice(0, 3).map((s, i) => (
            <span className="sp-stat__side" key={i}>
              <span className="sp-stat__side-val">{s.value == null ? '—' : formatCount(s.value)}</span>
              {s.label}
            </span>
          ))}
        </div>
      )}
      {measuredOn && (
        <div className="sp-stat__meta">
          <span>Measured</span><span className="sp-stat__date">{measuredOn}</span>
        </div>
      )}
    </div>
  );
}
